"use client";

import { useEffect, useRef } from "react";
import { withBasePath } from "@/lib/paths";

/**
 * Measures how long an ad slot was genuinely on screen, and records it once the visitor leaves it.
 *
 * "Impression" from an ad network means "we sent it"; this means "it was actually in front of
 * somebody". The difference is what makes revenue reports explicable — a slot below the fold that
 * nobody ever scrolled to counts as zero here, as it should.
 *
 * The clock stops when the tab is hidden, so a page left open in a background tab earns nothing.
 */
export function AdVisibility({ slot, children }: { slot: string; children: React.ReactNode }) {
  const box = useRef<HTMLDivElement>(null);
  const visibleSince = useRef<number | null>(null);
  const total = useRef(0);
  const reported = useRef(false);

  useEffect(() => {
    const node = box.current;
    if (!node) return;

    const sessionId = document.cookie.match(/murdoku_session=([^;]+)/)?.[1];

    const stop = (): void => {
      if (visibleSince.current === null) return;
      total.current += Date.now() - visibleSince.current;
      visibleSince.current = null;
    };
    const start = (): void => {
      if (visibleSince.current === null) visibleSince.current = Date.now();
    };

    const report = (kind: "impression" | "click"): void => {
      stop();
      if (kind === "impression") {
        if (reported.current || total.current === 0) return;
        reported.current = true;
      }
      const body = JSON.stringify({
        kind,
        slot,
        page: window.location.pathname,
        visibleMs: total.current,
        ...(sessionId ? { sessionId } : {}),
      });
      const url = withBasePath("/api/ad-event");
      // sendBeacon is the only send that survives the page being torn down — which is exactly
      // when the measurement is finished. keepalive fetch is the fallback where it is missing.
      if (navigator.sendBeacon?.(url, new Blob([body], { type: "application/json" }))) return;
      void fetch(url, { method: "POST", body, keepalive: true, headers: { "Content-Type": "application/json" } });
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting && document.visibilityState === "visible") start();
        else stop();
      },
      // Half the slot on screen counts as seen — the same bar the industry uses.
      { threshold: 0.5 },
    );
    observer.observe(node);

    const onVisibility = (): void => {
      if (document.visibilityState === "visible") return;
      stop();
      report("impression");
    };
    document.addEventListener("visibilitychange", onVisibility);
    // pagehide is the one signal a full navigation reliably fires — React's cleanup may never run
    // when the browser is already tearing the document down.
    const onLeave = (): void => report("impression");
    window.addEventListener("pagehide", onLeave);

    // And a ceiling: a slot watched for a long time should be on the record long before the
    // visitor leaves, or a session that ends in a crash would leave no trace of it at all.
    const settle = setTimeout(() => report("impression"), 10_000);

    const onClick = (): void => report("click");
    node.addEventListener("click", onClick);

    return () => {
      clearTimeout(settle);
      observer.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onLeave);
      node.removeEventListener("click", onClick);
      report("impression");
    };
  }, [slot]);

  return (
    <div ref={box} data-testid={`ad-wrapper-${slot}`}>
      {children}
    </div>
  );
}
